import { formatDate, formatTime } from '@trellistry/platform/i18n';
import { Actions, Button, Card, CheckField, Counts as Tally, Field, Notice, PageHeader, TextField } from '@trellistry/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api } from '../api.js';
import { importFailure } from './import-messages.js';

/**
 * Bringing a church across from Breeze (design §16). The browser holds the files, and sends them again to bring them
 * across, so nothing uploaded is kept. A check writes nothing, and changing any answer means checking again: what
 * gets written is exactly what this page last showed, which the server confirms from the check's fingerprint. A whole
 * church goes across a few hundred people at a time, so if a run stops, checking again shows who's left to bring.
 */
interface Fields { nameCn?: string; baptised?: string; member?: string; away?: string; back?: string; currentStage?: string; dates?: string[]; known?: string[]; sensitive?: string[] }
type MatchAnswer = { samePerson: string } | { someoneNew: true };
interface Answers { fields?: Fields; dateOrder?: 'month_first' | 'day_first'; matches: Record<string, MatchAnswer> }

interface Counts {
  dateOrder: string | null;
  people: { rows: number; created: number; updated: number; unchanged: number; refused: number; held: number; skippedErased: number; notInFile: number };
  families: { created: number };
  contacts: { added: number; removed: number };
  notWritten: { notes: number; tags: number; peopleWithSensitiveValues: number; faithStages: number; unmappedColumns: string[] };
}
interface Stop { code: 'missing_columns'; columns: string[] }
interface StopDates { code: 'date_order_unclear' }
interface Preview {
  stopped: null;
  fingerprint: string;
  reconciliation: Counts;
  fields: Fields;
  churchColumns: string[];
  refused: { row: number; breezeId?: string; code: string; message: string }[];
  warnings: { row: number; breezeId: string; name: string; field: string; code: string; message: string }[];
  held: { row: number; breezeId: string; code: 'edited_here' | 'merged_records' | 'possible_match'; changed: string[]; name: string; dateOfBirth: string | null; candidates: { personId: string; name: string; dateOfBirth: string | null }[] }[];
  updated: { row: number; breezeId: string; name: string; changed: string[]; linked: boolean }[];
  notInFile: { breezeId: string; name: string }[];
  duplicates: { confidence: 'strong' | 'possible'; suggestedKeep: string; reasons: string[]; people: { breezeId: string; name: string; dateOfBirth: string | null; family: string | null }[] }[];
  tags: { name: string; people: number; unknown: number; maybeCutShort: boolean; empty: boolean }[];
  notesLeftOut: { blank: number; unknownPerson: number };
}
type Stopped = { stopped: Stop | StopDates; fields: Fields; churchColumns: string[] };
type Checked = Preview | Stopped;

type Files = { people: File | null; notes: File | null; tags: File | null };

/** What each of a church's own columns can be, in the office's words. */
const COLUMN_USES: { value: string; label: string }[] = [
  { value: '', label: 'Leave out for now' },
  { value: 'known', label: 'Keep on the person’s card' },
  { value: 'sensitive', label: 'Sensitive: hold back for now' },
  { value: 'nameCn', label: 'Chinese name' },
  { value: 'baptised', label: 'Baptism date' },
  { value: 'member', label: 'Membership date' },
  { value: 'away', label: 'Date they left' },
  { value: 'back', label: 'Date they came back' },
  { value: 'currentStage', label: 'Stage of their faith journey' },
];
const SINGLE_USES = ['nameCn', 'baptised', 'member', 'away', 'back', 'currentStage'] as const;

function useOf(fields: Fields, column: string): string {
  for (const k of SINGLE_USES) if (fields[k] === column) return k;
  if (fields.known?.includes(column)) return 'known';
  if (fields.sensitive?.includes(column)) return 'sensitive';
  return '';
}

function withUse(fields: Fields, column: string, use: string): Fields {
  const next: Fields = { ...fields, known: (fields.known ?? []).filter((c) => c !== column), sensitive: (fields.sensitive ?? []).filter((c) => c !== column) };
  for (const k of SINGLE_USES) if (next[k] === column) delete next[k];
  if (use === 'known' || use === 'sensitive') next[use] = [...(next[use] ?? []), column];
  else if (use) next[use as (typeof SINGLE_USES)[number]] = column;
  return next;
}

const CHANGED_WORDS: Record<string, string> = { given: 'given name', surname: 'surname', nameCn: 'Chinese name', knownAs: 'known as', gender: 'gender', dateOfBirth: 'birthdate', status: 'status', membershipType: 'membership', attrs: 'other details', contacts: 'contact details', family: 'family' };
const changedWords = (changed: string[]) => changed.map((c) => CHANGED_WORDS[c] ?? c).join(', ');
const HELD_WORDS: Record<string, string> = {
  edited_here: 'Changed here since the last import, and the file says something different, so it’s left as it is here.',
  merged_records: 'These Breeze records were merged here, so they’re left alone rather than overwriting each other.',
  possible_match: 'This may be someone already here. Please say which.',
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A long list stays readable, and says what it isn't showing. */
function Lines<T>({ items, render }: { items: T[]; render: (item: T) => ReactNode }) {
  const LIMIT = 200;
  return (
    <>
      <ul className="lines">{items.slice(0, LIMIT).map((item, i) => <li key={i}>{render(item)}</li>)}</ul>
      {items.length > LIMIT && <p className="muted">And {items.length - LIMIT} more.</p>}
    </>
  );
}

/** One of the open-and-close sections of what a check found. */
function Section({ title, open, note, children }: { title: ReactNode; open?: boolean; note?: ReactNode; children: ReactNode }) {
  return (
    <details open={open}>
      <summary>{title}</summary>
      {note && <p className="muted">{note}</p>}
      {children}
    </details>
  );
}

function Totals({ c }: { c: Counts }) {
  return (
    <Tally items={[
      { label: 'Rows in the People file', count: c.people.rows },
      { label: 'People to add', count: c.people.created },
      { label: 'People to update', count: c.people.updated },
      { label: 'People already up to date', count: c.people.unchanged },
      { label: 'Rows that can’t be imported', count: c.people.refused },
      { label: 'Rows held for you to decide', count: c.people.held },
      ...(c.people.skippedErased > 0 ? [{ label: 'Rows for people erased here, left out', count: c.people.skippedErased }] : []),
      { label: 'People here who aren’t in this file', count: c.people.notInFile },
      { label: 'Families to add', count: c.families.created },
      { label: 'Contact details to add', count: c.contacts.added },
      { label: 'Contact details to remove', count: c.contacts.removed },
    ]} />
  );
}

/** What a run wrote, summed across its parts. */
interface Written { added: number; updated: number; families: number; contactsAdded: number; contactsRemoved: number }
const nothingWritten: Written = { added: 0, updated: 0, families: 0, contactsAdded: 0, contactsRemoved: 0 };
const plus = (w: Written, c: Counts): Written => ({ added: w.added + c.people.created, updated: w.updated + c.people.updated, families: w.families + c.families.created, contactsAdded: w.contactsAdded + c.contacts.added, contactsRemoved: w.contactsRemoved + c.contacts.removed });
/** The rest of what the office signs off comes from checking the file again once the run has finished. */
interface Finished { written: Written; check: Counts | null }

export function ImportPage() {
  const [files, setFiles] = useState<Files>({ people: null, notes: null, tags: null });
  const [answers, setAnswers] = useState<Answers>({ matches: {} });
  const [checked, setChecked] = useState<Checked | null>(null);
  const [checkedAnswers, setCheckedAnswers] = useState<string>('');
  const [busy, setBusy] = useState<'checking' | 'bringing' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<Finished | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [history, setHistory] = useState<{ id: string; status: string; created_at: string; people: Counts['people'] | null }[]>([]);

  useEffect(() => {
    api<{ batches: typeof history }>('/import/batches').then((r) => setHistory(r.batches)).catch(() => setHistory([]));
  }, [result]);

  const send = (path: string, extra: Record<string, unknown> = {}) => {
    const form = new FormData();
    form.set('people', files.people!);
    if (files.notes) form.set('notes', files.notes);
    if (files.tags) form.set('tags', files.tags);
    form.set('answers', JSON.stringify({ ...answers, ...extra }));
    return api<Record<string, unknown>>(path, { method: 'POST', body: form });
  };

  const check = async () => {
    setBusy('checking'); setProblem(null); setResult(null);
    try {
      const answer = (await send('/import/preview')) as unknown as Checked;
      setChecked(answer);
      setCheckedAnswers(JSON.stringify(answers));
      // the mapping starts from what the server used, the church's last import or the assumed columns
      setAnswers((a) => (a.fields ? a : { ...a, fields: answer.fields }));
    } catch (e) {
      setChecked(null);
      setProblem(importFailure(e, 'We couldn’t check these files just now. Please try again.'));
    } finally {
      setBusy(null);
    }
  };

  const bringAcross = async () => {
    if (!checked || checked.stopped) return;
    setBusy('bringing'); setProblem(null);
    const total = checked.reconciliation.people.created + checked.reconciliation.people.updated;
    let written = nothingWritten;
    setProgress({ done: 0, total });
    try {
      // each reply says what remains and carries its fingerprint for the next part, so the page keeps no count of its own
      let expected: string | null = checked.fingerprint;
      let run: { id: string; part: number } | undefined;
      while (expected) {
        const part = (await send('/import/apply', { expected, ...(run ? { run } : {}) })) as unknown as { reconciliation: Counts; remaining: number; next: string | null; run: { id: string; part: number } };
        written = plus(written, part.reconciliation);
        setProgress({ done: written.added + written.updated, total });
        expected = part.next;
        run = part.run;
      }
      // the sign-off: checking the file once more should find nothing left to do
      const after = (await send('/import/preview')) as unknown as Checked;
      setResult({ written, check: after.stopped ? null : after.reconciliation });
      setChecked(null);
      window.scrollTo(0, 0);
    } catch (e) {
      setChecked(null);
      const done = written.added + written.updated;
      setProblem(done > 0
        ? `This stopped after ${plural(done, 'person', 'people')} of ${total}. They’re safely across. Please check the files again to see who’s left, and carry on from there.`
        : importFailure(e, 'We couldn’t finish this. Nothing was changed. Please try again.'));
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const chooseFile = (key: keyof Files, f: File | null) => {
    setFiles((x) => ({ ...x, [key]: f }));
    setChecked(null); setResult(null); setProblem(null);
    setAnswers((a) => ({ ...a, matches: {} }));   // an answer about a row belongs to the file it was given for
  };

  // the first check fills in the mapping, which isn't a change of mind
  const answersChanged = !!checked && JSON.stringify({ ...answers, fields: answers.fields ?? checked.fields }) !== JSON.stringify({ ...JSON.parse(checkedAnswers || '{}'), fields: (JSON.parse(checkedAnswers || '{}') as Answers).fields ?? checked.fields });
  const preview = checked && !checked.stopped ? checked : null;
  const fields = answers.fields ?? checked?.fields ?? {};
  const matchOf = (id: string) => answers.matches[id];
  const setMatch = (id: string, a: MatchAnswer) => setAnswers((x) => ({ ...x, matches: { ...x.matches, [id]: a } }));
  const unanswered = preview?.held.filter((h) => h.code === 'possible_match').length ?? 0;
  const toBring = preview ? preview.reconciliation.people.created + preview.reconciliation.people.updated : 0;

  return (
    <div className="reading-width">
      <PageHeader title="Import from Breeze" />

      {result && (
        <Card title="Brought across">
          <Tally items={[
            { label: 'People added', count: result.written.added },
            { label: 'People updated', count: result.written.updated },
            { label: 'Families added', count: result.written.families },
            { label: 'Contact details added', count: result.written.contactsAdded },
            { label: 'Contact details removed', count: result.written.contactsRemoved },
          ]} />
          {result.check ? (
            <>
              <h2 className="section-gap">Checked against the file afterwards</h2>
              <Tally items={[
                { label: 'Rows in the People file', count: result.check.people.rows },
                { label: 'People up to date with it', count: result.check.people.unchanged },
                { label: 'Still to bring across', count: result.check.people.created + result.check.people.updated },
                { label: 'Rows that can’t be imported', count: result.check.people.refused },
                { label: 'Rows held for you to decide', count: result.check.people.held },
                { label: 'People here who aren’t in this file', count: result.check.people.notInFile },
              ]} />
              {result.check.notWritten.notes + result.check.notWritten.tags > 0 && <p className="muted">{plural(result.check.notWritten.notes, 'note')} and {plural(result.check.notWritten.tags, 'tag')} weren’t brought across, because they aren’t ready yet.</p>}
            </>
          ) : <p className="muted">We couldn’t check the file again afterwards. Please check it once more.</p>}
          <Actions className="no-print">
            <Button variant="quiet" onClick={() => window.print()}>Print this for sign-off</Button>
            <Link to="/people">See the people</Link>
          </Actions>
        </Card>
      )}

      <Card title="Choose your Breeze exports" className="no-print">
        <p className="muted">In Breeze, go to Account Settings, then Export. Checking a file changes nothing, and the files aren’t kept.</p>
        <TextField label="People (needed)" type="file" accept=".xlsx,.csv" onChange={(e) => chooseFile('people', e.target.files?.[0] ?? null)} />
        <TextField label="Notes (to check)" type="file" accept=".xlsx,.csv" onChange={(e) => chooseFile('notes', e.target.files?.[0] ?? null)} />
        <TextField label="Tags (to check)" type="file" accept=".xlsx" onChange={(e) => chooseFile('tags', e.target.files?.[0] ?? null)} />
        <Actions>
          <Button disabled={!files.people || busy !== null} onClick={check}>{busy === 'checking' ? 'Checking…' : checked ? 'Check again' : 'Check these files'}</Button>
        </Actions>
      </Card>

      {problem && <Notice tone="error">{problem}</Notice>}

      {checked?.stopped?.code === 'missing_columns' && (
        <Notice tone="error">
          This file is missing {(checked.stopped as Stop).columns.join(', ')}, so it can’t be read. Please export People again with those fields ticked.
        </Notice>
      )}

      {checked?.stopped?.code === 'date_order_unclear' && (
        <Card title="How does this file write dates?" tone="attention">
          <p>We can’t tell from the file. If it says 03/02/1985, does that mean:</p>
          <CheckField type="radio" name="dates" label="2 March 1985" checked={answers.dateOrder === 'month_first'} onChange={() => setAnswers((a) => ({ ...a, dateOrder: 'month_first' }))} />
          <CheckField type="radio" name="dates" label="3 February 1985" checked={answers.dateOrder === 'day_first'} onChange={() => setAnswers((a) => ({ ...a, dateOrder: 'day_first' }))} />
          <p>If you’re not sure, look up someone whose birthday you know. A wrong answer would swap days and months across the whole church.</p>
          <Button disabled={!answers.dateOrder || busy !== null} onClick={check}>Check again</Button>
        </Card>
      )}

      {preview && (
        <>
          <Card title="What would happen">
            <Totals c={preview.reconciliation} />
            {preview.duplicates.length > 0 && <p className="muted">This file has {plural(preview.duplicates.length, 'possible duplicate')}, kept as separate records for someone to look at afterwards. Nothing is merged by an import.</p>}
          </Card>

          {checked!.churchColumns.length > 0 && (
            <Card title="Your church’s own columns">
              <p className="muted">Breeze doesn’t define these, so please say what each one is. A column left out isn’t brought across, in case it holds something private.</p>
              <div className="mapping">
                {checked!.churchColumns.map((c) => (
                  <div key={c}>
                    <label htmlFor={`col-${c}`}>{c}</label>
                    <select id={`col-${c}`} value={useOf(fields, c)} onChange={(e) => setAnswers((a) => ({ ...a, fields: withUse(a.fields ?? checked!.fields, c, e.target.value) }))}>
                      {COLUMN_USES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {preview.held.length > 0 && (
            <Card title={`${plural(preview.held.length, 'row')} held for you`}>
              <Lines items={preview.held} render={(h) => (
                <>
                  <strong>{h.name || `Breeze ID ${h.breezeId}`}</strong>{h.dateOfBirth && <span className="muted"> · born {h.dateOfBirth}</span>}
                  <small>Row {h.row}. {HELD_WORDS[h.code]}{h.changed.length > 0 && ` The file differs on: ${changedWords(h.changed)}.`}</small>
                  {h.code === 'possible_match' && (
                    <>
                      {h.candidates.map((p) => (
                        <CheckField
                          key={p.personId}
                          type="radio"
                          name={`match-${h.breezeId}`}
                          checked={(matchOf(h.breezeId) as { samePerson?: string } | undefined)?.samePerson === p.personId}
                          onChange={() => setMatch(h.breezeId, { samePerson: p.personId })}
                          label={<>The same person as <Link to={`/people/${p.personId}`} target="_blank">{p.name}</Link>{p.dateOfBirth ? `, born ${p.dateOfBirth}` : ''}. The file’s details would replace theirs.</>}
                        />
                      ))}
                      <CheckField type="radio" name={`match-${h.breezeId}`} checked={!!matchOf(h.breezeId) && 'someoneNew' in matchOf(h.breezeId)!} onChange={() => setMatch(h.breezeId, { someoneNew: true })} label="Someone new: add a separate record" />
                    </>
                  )}
                </>
              )} />
            </Card>
          )}

          <Card>
            {preview.refused.length > 0 && (
              <Section open title={`${plural(preview.refused.length, 'row')} that can’t be imported`}>
                <Lines items={preview.refused} render={(r) => <>Row {r.row}{r.breezeId ? ` (Breeze ID ${r.breezeId})` : ''}<small>{r.message}</small></>} />
              </Section>
            )}
            {preview.warnings.length > 0 && (
              <Section title={`${plural(preview.warnings.length, 'thing')} to look at`} note="These people still come across. A value we can’t read is left out, and never removes one we already hold.">
                <Lines items={preview.warnings} render={(w) => <>{w.name || `Breeze ID ${w.breezeId}`} · {w.field}<small>Row {w.row}. {w.message}</small></>} />
              </Section>
            )}
            {preview.updated.length > 0 && (
              <Section title={`${plural(preview.updated.length, 'person', 'people')} to update`}>
                <Lines items={preview.updated} render={(u) => <>{u.name}<small>Row {u.row}. {u.linked ? 'Ties this Breeze record to them. ' : ''}{u.changed.length > 0 ? `Changes: ${changedWords(u.changed)}.` : ''}</small></>} />
              </Section>
            )}
            {preview.duplicates.length > 0 && (
              <Section title={plural(preview.duplicates.length, 'possible duplicate')}>
                <Lines items={preview.duplicates} render={(g) => <>{g.people.map((p) => `${p.name}${p.dateOfBirth ? ` (${p.dateOfBirth})` : ''}`).join(', ')}<small>{g.confidence === 'strong' ? 'Likely' : 'Possibly'} the same person: {g.reasons.join(', ')}.</small></>} />
              </Section>
            )}
            {preview.notInFile.length > 0 && (
              <Section title={`${plural(preview.notInFile.length, 'person', 'people')} here who aren’t in this file`} note="They stay as they are. An import never removes anyone.">
                <Lines items={preview.notInFile} render={(p) => <>{p.name}<small>Breeze ID {p.breezeId}</small></>} />
              </Section>
            )}
            <Section title="What isn’t brought across yet">
              <ul className="lines">
                <li>{plural(preview.reconciliation.notWritten.notes, 'note')}<small>Read and checked. They’ll come across once it’s settled who may read them.</small></li>
                <li>{plural(preview.reconciliation.notWritten.tags, 'tag')}<small>{preview.tags.map((t) => `${t.name} (${t.people})`).join(', ') || 'None in these files.'}</small></li>
                <li>{plural(preview.reconciliation.notWritten.peopleWithSensitiveValues, 'person', 'people')} with sensitive details<small>Held back until they can be stored encrypted.</small></li>
                <li>{plural(preview.reconciliation.notWritten.faithStages, 'faith journey date')}<small>Read and checked, not stored yet.</small></li>
                <li>Permission to contact<small>Breeze doesn’t record it, so nobody has permission on record after an import.</small></li>
                {preview.reconciliation.notWritten.unmappedColumns.length > 0 && <li>Columns left out<small>{preview.reconciliation.notWritten.unmappedColumns.join(', ')}</small></li>}
              </ul>
            </Section>
          </Card>

          <Card className="no-print">
            {answersChanged ? (
              <>
                <p>You’ve changed an answer, so please check again to see what would happen now.</p>
                <Button disabled={busy !== null} onClick={check}>{busy === 'checking' ? 'Checking…' : 'Check again'}</Button>
              </>
            ) : toBring === 0 ? (
              <p>Nothing here needs bringing across{unanswered > 0 ? ` until you answer about the ${plural(unanswered, 'row')} held above` : ', so everyone is up to date with this file'}.</p>
            ) : (
              <>
                <p className="muted">{unanswered > 0 ? `${plural(unanswered, 'row')} still held will stay out until you answer. ` : ''}You can import again later: people are matched by their Breeze ID, so nobody is added twice.</p>
                <Button disabled={busy !== null} onClick={bringAcross}>{busy === 'bringing' ? 'Bringing across…' : `Bring ${plural(toBring, 'person', 'people')} across`}</Button>
                {progress && (
                  <div className="section-gap">
                    <Field label={`${progress.done} of ${progress.total}. Please keep this page open. If it closes, those already across stay, and you can carry on.`}>
                      {(c) => <progress max={progress.total} value={progress.done} {...c} />}
                    </Field>
                  </div>
                )}
              </>
            )}
          </Card>
        </>
      )}

      {history.length > 0 && (
        <Card title="Earlier imports" className="no-print">
          <Lines items={history} render={(b) => (
            <>
              {formatDate(new Date(b.created_at))}, {formatTime(new Date(b.created_at))}
              <small>{b.status === 'failed' ? 'Couldn’t be finished, so nothing was changed.' : b.people ? `${b.people.created} added, ${b.people.updated} updated, ${b.people.refused} couldn’t be imported, ${b.people.held} held.` : ''}</small>
            </>
          )} />
        </Card>
      )}
    </div>
  );
}
